import { KlarnaPaymentMessageUI } from "./KlarnaPaymentMessageUI";
import { KlarnaPlaceholder } from "./KlarnaPlaceholder";
import { StandardProps } from "../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { CreditIllustration } from "@/types/cobeTypes";
import { getColourGroupForVariant } from "@/utils/product/getColourGroupForVariant.ts";
import { getVariantsForGroup } from "@/components/Colours/helpers.ts";
import { Variant } from "@/types/types.ts";

export function KlarnaPaymentMessage({
  product,
  creditOffersV2,
  runtimeConfig,
  flags,
}: Pick<
  StandardProps,
  "product" | "creditOffersV2" | "runtimeConfig" | "flags"
>) {
  const { isSelected, variant } = useVariant(product);

  const showKlarnaMessage = !!flags.showKlarnaMessage.value;

  const activeProduct = ensureIsActive(product);

  if (!activeProduct) {
    return null;
  }

  let amount: string | null = null;
  let showPlaceholder = false;

  if (isSelected) {
    const variantEligible = creditOffersV2?.some(
      ({ skuId, creditIllustrations }) => {
        if (skuId === variant?.aliases.skuId) {
          return creditIllustrations.some(
            (illustration: CreditIllustration) =>
              illustration.creditProvider?.toLowerCase() === "klarna",
          );
        }
        return false;
      },
    );
    amount = variantEligible
      ? String(Math.round(Number(variant.price?.value) * 100))
      : null;
  } else {
    const variantGroup = getColourGroupForVariant(
      product.variantGroups,
      variant.id,
    );

    const priceRange =
      variantGroup?.variantPriceRange || activeProduct?.variantPriceRange;

    const hasPriceRange =
      priceRange?.display?.min !== undefined &&
      priceRange?.display?.max !== undefined &&
      priceRange?.display?.min !== priceRange?.display?.max;

    const allVariants = variantGroup
      ? getVariantsForGroup(variantGroup, product)
      : product.variants;

    const variantGroupEligibleForKlarna = allVariants.some((variant: Variant) =>
      creditOffersV2.some(
        ({ skuId, creditIllustrations }) =>
          skuId === variant.aliases.skuId &&
          creditIllustrations.some(
            (illustration: CreditIllustration) =>
              illustration.creditProvider?.toLowerCase() === "klarna",
          ),
      ),
    );

    if (variantGroupEligibleForKlarna) {
      if (hasPriceRange) {
        showPlaceholder = true;
      } else {
        amount = String(Math.round(Number(priceRange?.value?.min) * 100));
      }
    }
  }

  if (!showKlarnaMessage) {
    return null;
  }

  if (showPlaceholder) {
    return <KlarnaPlaceholder />;
  }

  return (
    amount !== null && (
      <KlarnaPaymentMessageUI
        runtimeConfig={runtimeConfig}
        currentPrice={amount}
        useNewTypography={flags.useNewTypography.value === true}
      />
    )
  );
}
